import { useEffect, useRef, useCallback } from 'react'

const usePostMessageListener = ({ messageHandler, origin = '', selector = '' }) => {
  const expectedSourceRef = useRef<Window>(typeof window !== 'undefined' ? window : null)

  const setExpectedSourceRef = (
    expectedSource: HTMLIFrameElement | Document | Window
  ) => {
    if (!expectedSource) {
      expectedSourceRef.current = null
    } else if ('contentWindow' in expectedSource) {
      expectedSourceRef.current = expectedSource.contentWindow
    } else if ('defaultView' in expectedSource) {
      expectedSourceRef.current = expectedSource.defaultView
    } else if ('document' in expectedSource) {
      expectedSourceRef.current = expectedSource
    }
  }

  const hasPermittedOrigin = useCallback(
    (event: MessageEvent) => {
      const expectedOrigin = origin || window.location.origin
      const unprotectedOrigin = event.origin === '*'
      return unprotectedOrigin || event.origin === expectedOrigin
    },
    [origin]
  )

  useEffect(() => {
    if (selector) {
      const updateExpectedSourceRef = (event: MessageEvent) => {
        if (hasPermittedOrigin(event)) {
          setExpectedSourceRef(document.querySelector<HTMLIFrameElement>(selector))
        }
      }

      window.addEventListener('message', updateExpectedSourceRef, false)

      return () => {
        window.removeEventListener('message', updateExpectedSourceRef, false)
      }
    }
  }, [selector, hasPermittedOrigin])

  useEffect(() => {
    const onMessageHandler = (event: MessageEvent) => {
      const isFromExpectedSource = event.source === expectedSourceRef.current
      const shouldMessageBeHandled = hasPermittedOrigin(event) && isFromExpectedSource
      if (shouldMessageBeHandled) {
        return messageHandler(event.data)
      }
    }

    window.addEventListener('message', onMessageHandler, false)

    return () => {
      window.removeEventListener('message', onMessageHandler, false)
    }
  }, [messageHandler, hasPermittedOrigin])

  return { ref: setExpectedSourceRef, expectedSourceRef }
}

export default usePostMessageListener
